import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Timer,
  Radio,
  ArrowRight,
  Package,
  Handshake,
  Rocket,
  Lightbulb,
} from "lucide-react";
import HomeOnboardingDialog from "../components/home/HomeOnboardingDialog";
import { useUserPref } from "../lib/useUserPref";
import { useAuth } from "../context/AuthContext";

const SECTIONS = [
  {
    to: "/prodotti",
    icon: Package,
    label: "Prodotti",
    desc: "Libreria, Corsi, Risorse, Guide, Manuali, Dossier.",
  },
  {
    to: "/servizi",
    icon: Handshake,
    label: "Servizi",
    desc: "Agenti AI, Consulenze, Sessioni.",
  },
  {
    to: "/percorsi",
    icon: Rocket,
    label: "Percorsi",
    desc: "TIME IMMERSION · TRASFOPRODUCT 6/12 mesi.",
  },
  {
    to: "/innovazione",
    icon: Lightbulb,
    label: "Innovazione",
    desc: "TIME Watch · Il tempo come strumento.",
  },
];

export default function HomePage() {
  // Onboarding: unico popup POST-LOGIN che indirizza a Innovazione → TIME
  // Watch → modalità TIME Watch → Centro Guida in alto a destra. Persistenza
  // per-utente via useUserPref (cloud + localStorage).
  //
  // Il popup NON deve comparire per utenti non autenticati: la Home è anche
  // la landing pubblica, l'onboarding TIME Inside è riservato ai loggati.
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [onboardingDismissed, setOnboardingDismissed, prefReady] = useUserPref(
    "homeOnboardingDismissed",
    false
  );
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setOnboardingOpen(false);
      return;
    }
    if (!prefReady) return;
    // Ephemeral one-shot: consente di riaprire l'onboarding dall'Area
    // Personale ("Rivedi onboarding") anche se homeOnboardingDismissed=true,
    // senza modificare quella preferenza né i login successivi.
    let forceOpen = false;
    try {
      forceOpen =
        sessionStorage.getItem("timeInside:onboardingForceOpen") === "1";
      if (forceOpen) sessionStorage.removeItem("timeInside:onboardingForceOpen");
    } catch {
      /* sessionStorage non disponibile: fallback ignorato */
    }
    if (forceOpen || !onboardingDismissed) setOnboardingOpen(true);
  }, [authLoading, isAuthenticated, prefReady, onboardingDismissed]);

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-7xl mx-auto">
      {/* Hero */}
      <section className="mb-24 fade-in text-center" data-testid="home-hero">
        <div
          data-testid="home-hero-claim"
          className="inline-flex items-center gap-3 mb-4 text-[11px] sm:text-xs tracking-[0.32em] uppercase text-emerald-400/80"
        >
          <span className="w-6 h-px bg-emerald-400/40" />
          TIME - Lascia il Segno
          <span className="w-6 h-px bg-emerald-400/40" />
        </div>
        <div
          data-testid="home-hero-subtitle"
          className="mb-8 text-base sm:text-lg font-light text-white/90 tracking-tight"
        >
          È solo questione di Tempo.
        </div>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extralight tracking-tight text-white leading-[1.02]">
          <span className="block">
            <span className="text-emerald-400">Il Tempo</span>
            <span className="text-white"> è la tua</span>
          </span>
          <span className="block text-amber-300">vera ricchezza.</span>
        </h1>
        <p className="mt-6 text-lg font-light text-white/50 max-w-2xl mx-auto leading-relaxed">
          TIME Inside è il contenitore dell&apos;intero ecosistema TIME: prodotti,
          servizi, percorsi e innovazione. Un solo luogo per trasformare denaro,
          conoscenza e tempo in valore reale.
        </p>

        {/* Impronta interattiva — invito allo scroll */}
        <FingerprintScrollHint targetId="in-primo-piano" />
      </section>

      {/* In Primo Piano */}
      <section
        id="in-primo-piano"
        data-testid="in-primo-piano"
        className="mb-24"
        aria-labelledby="in-primo-piano-title"
      >
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase text-white/40 mb-2">
              In Primo Piano
            </div>
            <h2
              id="in-primo-piano-title"
              className="text-2xl sm:text-3xl font-light text-white"
            >
              Il Mio Tempo è anche il Tuo.
            </h2>
          </div>
        </div>

        <div className="grid">
          {/* TIMER LAPSE — include Live Gratuite */}
          <Link
            to="/timer-lapse"
            data-testid="card-timer-lapse"
            className="glass-card p-8 sm:p-12 relative overflow-hidden group block"
          >
            <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-emerald-400/[0.06] blur-3xl pointer-events-none" />
            <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-amber-400/[0.05] blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center">
                  <Timer className="w-5 h-5 text-emerald-300" />
                </div>
                <span className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/80">
                  TIMER LAPSE
                </span>
              </div>

              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extralight text-white leading-[1.1] tracking-tight">
                Cos&apos;è il{" "}
                <span className="text-emerald-400">Tempo</span> per te? Parliamone.
              </h3>

              <div className="mt-10 pt-8 border-t border-white/5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-400/10 border border-amber-400/25 flex items-center justify-center shrink-0">
                  <Radio className="w-4 h-4 text-amber-300" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.32em] uppercase text-amber-300/80 mb-1">
                    Live Gratuite
                  </div>
                  <p
                    data-testid="live-gratuite-desc"
                    className="text-sm text-white/55 font-light leading-relaxed max-w-xl"
                  >
                    Uno spazio dedicato per promuovere e ospitare le live TIMER
                    LAPSE a titolo gratuito. Aperte a tutti.
                  </p>
                </div>
              </div>

              <div className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />
                Presto disponibile
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Sezioni principali */}
      <section data-testid="sezioni-principali">
        <div className="text-[11px] tracking-[0.32em] uppercase text-white/40 mb-8">
          Esplora l&apos;ecosistema
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.to}
                to={s.to}
                data-testid={`home-section-${s.label.toLowerCase()}`}
                className="glass-card p-6 group hover:-translate-y-1 transition-all duration-500"
              >
                <div className="flex items-center justify-between mb-6">
                  <Icon className="w-5 h-5 text-white/60 group-hover:text-emerald-300 transition" />
                  <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/70 group-hover:translate-x-1 transition-all" />
                </div>
                <div className="text-lg font-light text-white mb-2">
                  {s.label}
                </div>
                <div className="text-xs font-light text-white/45 leading-relaxed">
                  {s.desc}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <HomeOnboardingDialog
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        onDontShowAgain={(v) => setOnboardingDismissed(!!v)}
      />
    </div>
  );
}

/**
 * FingerprintScrollHint — impronta digitale verde LED (asset ufficiale
 * `/images/fingerprint-green.png`, PNG con sfondo trasparente).
 *
 * Doppia funzione:
 *   • TAP/CLICK breve → apre TIME Watch nella funzione "Tempo Libero"
 *     (schermata 04 del carosello, `#page-3`).
 *   • Drag/scroll verticale sopra l'impronta → NON naviga, lascia scorrere
 *     la Home normalmente. Questo è cruciale su mobile: tap ≠ scroll.
 *
 * Rispetta `prefers-reduced-motion` per pulse/scroll.
 */
function FingerprintScrollHint({ targetId: _targetId }) {
  const navigate = useNavigate();
  const pointerStateRef = React.useRef({ startX: 0, startY: 0, startTs: 0, cancelled: false });

  const openTempoLibero = React.useCallback(() => {
    navigate("/innovazione/time-watch/pmv#page-3");
  }, [navigate]);

  // Soglia oltre la quale il pointer è considerato "drag/scroll" e non tap.
  const TAP_MAX_MOVE_PX = 15;
  const TAP_MAX_DURATION_MS = 700;

  const onPointerDown = (e) => {
    pointerStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startTs: Date.now(),
      cancelled: false,
    };
  };
  const onPointerMove = (e) => {
    const s = pointerStateRef.current;
    if (s.cancelled) return;
    const dx = Math.abs(e.clientX - s.startX);
    const dy = Math.abs(e.clientY - s.startY);
    if (dx > TAP_MAX_MOVE_PX || dy > TAP_MAX_MOVE_PX) {
      // pointer si è mosso troppo → è uno scroll/drag, annulla il tap
      pointerStateRef.current.cancelled = true;
    }
  };
  const onPointerCancel = () => {
    pointerStateRef.current.cancelled = true;
  };
  const onClick = (e) => {
    const s = pointerStateRef.current;
    // Se non abbiamo intercettato un pointerdown coerente (es. tastiera),
    // lasciamo procedere la navigazione (accessibilità).
    if (!s.startTs) {
      openTempoLibero();
      return;
    }
    const duration = Date.now() - s.startTs;
    if (s.cancelled || duration > TAP_MAX_DURATION_MS) {
      // era uno scroll/long-press: NON navigare
      e.preventDefault();
      return;
    }
    openTempoLibero();
  };

  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerCancel={onPointerCancel}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openTempoLibero();
        }
      }}
      data-testid="home-hero-scroll-hint"
      aria-label="Apri TIME Watch — Tempo Libero"
      className="mt-14 sm:mt-16 inline-flex items-center justify-center rounded-full p-2 bg-transparent transition-transform duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black motion-safe:animate-[time-print-pulse_3.4s_ease-in-out_infinite]"
      style={{
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <img
        src="/images/fingerprint-green.png"
        alt=""
        aria-hidden="true"
        draggable={false}
        className="w-auto h-[105px] sm:h-[118px] lg:h-[128px] select-none pointer-events-none"
        style={{
          filter:
            "drop-shadow(0 0 6px rgba(34,197,94,0.28)) drop-shadow(0 0 18px rgba(34,197,94,0.14))",
        }}
      />
    </button>
  );
}
